/**
 * 주문 국가몰(country-mall) — be-order-mall 계약(order-service)의 국가몰 코드를 BO
 * 주문관리(검색 필터 · 상세 표시 · 엑셀 export)에서 친화 라벨로 노출하기 위한 단일 소스.
 *
 * ★코드 = BCP-47 `ko-KR/ja-JP/en-US/zh-CN`(하이픈) — be-order-mall 최종 와이어 포맷 정본.
 * 조율 정본(00-COORDINATION §몰 코드)·코드베이스 노출몰(EXPOSED/COLLECTION/ORDER_FORM_MALLS)·
 * PaymentLocale 과 동일 BCP-47. BE enum name(KO_KR)은 DB 내부 전용이라 API 에는 BCP-47 만 나온다.
 * BE 응답엔 `mall`(BCP-47 코드)+`mallLabel`(한국어 라벨, ALWAYS)이 채워지나, FE 는 UI 언어별
 * 로컬라이즈를 위해 **코드를 키로 자체 i18n 라벨을 매핑**한다(mallLabel 은 한국어 단일이라 미사용).
 *
 * - 필터바: `t('orders.mall.${code}')` 로 i18n 라벨(결제수단/주문상태 필터와 동일 패턴).
 * - 상세/목록: `labelOf(ORDER_MALL_LABELS, code)` 로 raw-key-safe 라벨
 *   (lib/enumLabels 의 labelOf 와 동일 — 미지원/누락 코드는 '-' 폴백, 코드 원문 미노출).
 */
import type { LabelMap } from '@/lib/enumLabels';

/** 국가몰 코드(정본 순서) — be-order-mall `mall` 응답/필터/export 값과 1:1(BCP-47). */
export const ORDER_MALLS = ['ko-KR', 'ja-JP', 'en-US', 'zh-CN'] as const;
export type OrderMall = (typeof ORDER_MALLS)[number];

/**
 * 친화 라벨 — Lead 조율 정본(00-COORDINATION.md) + be-order-mall label() 정합:
 * 한국몰/일본몰/영문몰/중문몰. detail/목록의 raw-key-safe 표기에 사용(미지원 코드는
 * labelOf 가 '-' 폴백). i18n 4로케일(`orders.mall.*`)과 동일 한글 라벨.
 */
export const ORDER_MALL_LABELS: LabelMap = {
  'ko-KR': '한국몰',
  'ja-JP': '일본몰',
  'en-US': '영문몰',
  'zh-CN': '중문몰',
};

const ORDER_MALL_SET: ReadonlySet<string> = new Set<string>(ORDER_MALLS);

/** 알 수 없는/누락 값을 거른 국가몰 코드 narrowing(응답 방어 — 미지원 코드는 null). */
export const asOrderMall = (value: string | null | undefined): OrderMall | null =>
  value != null && ORDER_MALL_SET.has(value) ? (value as OrderMall) : null;
